/**
 * IconColorPicker + MaterialVisual — gedeeld door materialen (types) en sets.
 * Kies een icoon + kleur als visuele identiteit wanneer er geen foto is.
 */
import React, { useState } from 'react';
import { Box, Typography, Popover, Tooltip } from '@mui/material';

const T = {
  primary: '#526075', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...sx }}>{name}</span>
);

// 9 vaste kleuren: rood/oranje/geel/groen/blauw/paars/roze/zwart/wit
export const PRESET_COLORS = [
  { name: 'Rood', hex: '#ef4444' },
  { name: 'Oranje', hex: '#f97316' },
  { name: 'Geel', hex: '#eab308' },
  { name: 'Groen', hex: '#10b981' },
  { name: 'Blauw', hex: '#3b82f6' },
  { name: 'Paars', hex: '#8b5cf6' },
  { name: 'Roze', hex: '#ec4899' },
  { name: 'Zwart', hex: '#2d3335' },
  { name: 'Wit', hex: '#ffffff' },
];

export const PICKER_ICONS = [
  'videocam', 'photo_camera', 'camera', 'movie', 'lens_blur', 'monitor', 'tv',
  'mic', 'headphones', 'speaker', 'surround_sound', 'lightbulb', 'wb_incandescent',
  'flash_on', 'bolt', 'battery_full', 'cable', 'settings_input_component', 'memory',
  'sd_card', 'usb', 'handyman', 'build', 'straighten', 'flight', 'local_shipping',
  'storefront', 'auto_awesome', 'backpack', 'inventory_2', 'category', 'hardware',
];

const DEFAULT_COLOR = '#526075';

/** Display-tegel: foto → anders icoon+kleur → anders fallback-categorie-icoon. */
export function MaterialVisual({ imageUrl, icon, color, fallbackIcon = 'inventory_2', radius = '0.5rem', iconSize = 22, sx = {} }) {
  if (imageUrl) {
    return <Box component="img" src={imageUrl} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: radius, ...sx }} />;
  }
  const bg = color || T.surfaceLow;
  const lc = (color || '').toLowerCase();
  // Leesbare icoonkleur: wit → grijs, geel → donkerder amber, anders de kleur zelf
  const iconColor = !color ? T.outlineVariant
    : lc === '#ffffff' ? T.outline
    : lc === '#eab308' ? '#a16207'
    : bg;
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: color ? `${bg}1f` : T.surfaceLow, borderRadius: radius, ...sx }}>
      <Icon name={icon || fallbackIcon} size={iconSize} sx={{ color: iconColor }} />
    </Box>
  );
}

/** Editor: tegel die een Popover opent met kleur-swatches + icoon-grid. */
export default function IconColorPicker({ icon, color, onChange, label = 'Icoon & kleur' }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const activeColor = color || DEFAULT_COLOR;

  const setColor = (hex) => onChange?.({ icon: icon || 'inventory_2', color: hex });
  const setIcon = (name) => onChange?.({ icon: name, color: activeColor });

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Tooltip title={label} arrow>
          <Box onClick={(e) => setAnchor(e.currentTarget)} sx={{
            width: 56, height: 56, borderRadius: '0.7rem', cursor: 'pointer', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${activeColor}1f`, border: `1.5px solid ${activeColor}55`,
            transition: 'all 0.15s', '&:hover': { borderColor: activeColor },
          }}>
            <Icon name={icon || 'add_photo_alternate'} size={26} sx={{ color: activeColor }} />
          </Box>
        </Tooltip>
        <Box>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>{label}</Typography>
          <Typography onClick={(e) => setAnchor(e.currentTarget)} sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            {icon ? 'Wijzigen' : 'Kies icoon & kleur'}
          </Typography>
        </Box>
      </Box>

      <Popover open={open} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, borderRadius: '0.9rem', width: 300, boxShadow: '0 10px 40px rgba(45,51,53,0.18)' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kleur</Typography>
          {(icon || color) && (
            <Box onClick={() => onChange?.({ icon: null, color: null })} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', color: T.outline, '&:hover': { color: T.text } }}>
              <Icon name="restart_alt" size={13} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700 }}>Wissen</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {PRESET_COLORS.map(c => (
            <Tooltip key={c.hex} title={c.name} arrow>
              <Box onClick={() => setColor(c.hex)} sx={{
                width: 26, height: 26, borderRadius: '50%', cursor: 'pointer', bgcolor: c.hex,
                border: c.hex === '#ffffff' ? `1px solid ${T.surfaceHigh}` : 'none',
                outline: activeColor.toLowerCase() === c.hex ? `2px solid ${T.primary}` : 'none', outlineOffset: 2,
                transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.12)' },
              }} />
            </Tooltip>
          ))}
          <Tooltip title="Eigen kleur" arrow>
            <Box component="label" sx={{
              width: 26, height: 26, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'conic-gradient(red, orange, yellow, green, cyan, blue, magenta, red)', position: 'relative',
            }}>
              <input type="color" value={activeColor} onChange={(e) => setColor(e.target.value)} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <Icon name="colorize" size={14} sx={{ color: '#fff' }} />
            </Box>
          </Tooltip>
        </Box>

        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>Icoon</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, maxHeight: 180, overflow: 'auto' }}>
          {PICKER_ICONS.map(name => {
            const selected = icon === name;
            return (
              <Box key={name} onClick={() => setIcon(name)} sx={{
                aspectRatio: '1', borderRadius: '0.45rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                bgcolor: selected ? `${activeColor}1f` : T.surfaceLow,
                outline: selected ? `2px solid ${activeColor}` : 'none', outlineOffset: -2,
                transition: 'all 0.1s', '&:hover': { bgcolor: T.surfaceHigh },
              }}>
                <Icon name={name} size={19} sx={{ color: selected ? activeColor : T.secondary }} />
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}
